import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ApiError } from "@/lib/api";
import { apiPersonaDocumentosAdapter } from "@/lib/persona-documentos-adapter";
import {
  construirSlots,
  resumirDocumentacionGeneral,
  type ResumenDocumentacionGeneral,
  type SlotDocumentacion,
} from "@/lib/persona-documentos-resumen";
import type { PersonaDocumentosVencimientoOptions } from "@/lib/persona-documentos-adapter";
import type { DocumentoTipo } from "@/types/documento-tipo";
import type { PersonaDocumento } from "@/types/persona-documento";

/**
 * Estado y acciones de la Documentación General de un trabajador: el catálogo
 * de tipos cruzado con los documentos cargados, más subir / reemplazar /
 * descargar / eliminar / cambiar la vigencia.
 *
 * Existe como hook y no como estado suelto dentro del panel por dos razones:
 * el pliegue del resumen tiene que salir de UN solo lugar (ver
 * `lib/persona-documentos-resumen.ts`), y el panel quedó con una sola
 * responsabilidad —dibujar— en vez de mezclar carga, subida y layout como
 * hacía `DocumentosAdicionalesPanel`.
 */

const adapter = apiPersonaDocumentosAdapter;

function mensajeDeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.message) return err.message;
  return fallback;
}

function esCancelacion(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

export interface DocumentacionGeneral {
  slots: SlotDocumentacion[];
  resumen: ResumenDocumentacionGeneral;
  isLoading: boolean;
  loadError: string | null;
  recargar: () => void;
  /** Descarga / borrado / vigencia en curso para ese `tipo.id`. */
  busyTipoId: number | null;
  /** Progreso 0-100 por `tipo.id`; la clave sólo existe mientras sube. */
  uploadingPercents: Record<number, number>;
  subir: (tipo: DocumentoTipo, file: File) => Promise<void>;
  cancelarSubida: (tipoId: number) => void;
  descargar: (tipo: DocumentoTipo, documento: PersonaDocumento) => Promise<void>;
  eliminar: (tipo: DocumentoTipo, documento: PersonaDocumento) => Promise<void>;
  guardarVigencia: (
    tipo: DocumentoTipo,
    documento: PersonaDocumento,
    opts: PersonaDocumentosVencimientoOptions,
  ) => Promise<void>;
  /** Reemplaza el set de tipos que la empresa exige. */
  guardarExigidos: (tipoIds: number[]) => Promise<void>;
}

export interface UseDocumentacionGeneralOptions {
  /**
   * Ventana de "por vencer" del servidor (`meta.dias_por_vencer`). Mientras no
   * haya llegado se pasa `undefined` y el pliegue usa el default de
   * `lib/vencimiento.ts`; al llegar, los slots se recalculan solos porque es
   * dependencia del `useMemo`.
   */
  diasPorVencer?: number;
  /**
   * Se dispara cuando el conjunto de documentos cambió (subida, borrado,
   * cambio de vigencia). La Ficha lo usa para recargar `/personas/resumen`: sus
   * tres KPI salen del servidor, así que sin esto el encabezado seguiría
   * diciendo "12 cargados" después de subir el trece.
   */
  onChange?: () => void;
}

/** @param personaUuid Trabajador dueño de los documentos. */
export function useDocumentacionGeneral(
  personaUuid: string,
  { diasPorVencer, onChange }: UseDocumentacionGeneralOptions = {},
): DocumentacionGeneral {
  const [tipos, setTipos] = useState<DocumentoTipo[]>([]);
  // Bandera propia y no derivada de `tipos`: "no configuró" y "configuró y no
  // exige ninguno" dan los dos cero tipos con `exigido = true`, y la pantalla
  // los muestra distinto.
  const [exigiblesConfigurados, setExigiblesConfigurados] = useState(false);
  const [documentos, setDocumentos] = useState<PersonaDocumento[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyTipoId, setBusyTipoId] = useState<number | null>(null);
  const [uploadingPercents, setUploadingPercents] = useState<Record<number, number>>({});
  const abortControllersRef = useRef<Map<number, AbortController>>(new Map());

  // `onChange` casi siempre llega como arrow inline desde el JSX, o sea con
  // identidad nueva en cada render. Guardarlo en un ref lo saca de las deps de
  // los `useCallback` de abajo: si fuera dependencia, cada render recrearía las
  // acciones y con ellas los handlers de cada fila.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const cargar = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [tiposRes, documentosRes] = await Promise.all([
        adapter.listTipos(),
        adapter.listDocumentos(personaUuid),
      ]);
      setTipos(tiposRes.tipos);
      setExigiblesConfigurados(tiposRes.exigiblesConfigurados);
      setDocumentos(documentosRes);
    } catch (err) {
      setLoadError(mensajeDeError(err, "No se pudo cargar la documentación general."));
    } finally {
      setIsLoading(false);
    }
  }, [personaUuid]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Al desmontar (salir de la ficha) abortamos cualquier subida en curso:
  // evita subidas colgadas y setState sobre un componente desmontado.
  // `abortControllersRef` es un ref, así que el cleanup no necesita deps.
  useEffect(
    () => () => {
      abortControllersRef.current.forEach((controller) => controller.abort());
    },
    [],
  );

  const slots = useMemo(
    () => construirSlots(tipos, documentos, diasPorVencer),
    [tipos, documentos, diasPorVencer],
  );
  const resumen = useMemo(
    () => resumirDocumentacionGeneral(slots, exigiblesConfigurados),
    [slots, exigiblesConfigurados],
  );

  /**
   * Guarda el set de exigibles y se queda con el catálogo que devuelve el PUT
   * — no dispara un refetch: la respuesta YA es el catálogo actualizado, y
   * pedirlo de nuevo abriría una ventana en la que la pantalla muestra el
   * estado viejo.
   *
   * `onChange` también corre: el KPI del encabezado se calcula server-side y
   * su denominador acaba de cambiar.
   */
  const guardarExigidos = useCallback(async (tipoIds: number[]) => {
    const catalogo = await adapter.guardarExigidos(tipoIds);
    setTipos(catalogo.tipos);
    setExigiblesConfigurados(catalogo.exigiblesConfigurados);
    toast.success("Listo, guardamos qué documentos exige tu empresa.");
    onChangeRef.current?.();
  }, []);

  const subir = useCallback(
    async (tipo: DocumentoTipo, file: File) => {
      const controller = new AbortController();
      abortControllersRef.current.set(tipo.id, controller);
      setUploadingPercents((prev) => ({ ...prev, [tipo.id]: 0 }));
      try {
        const documento = await adapter.upload(personaUuid, tipo.id, file, {
          onProgress: (percent) =>
            setUploadingPercents((prev) => ({ ...prev, [tipo.id]: percent })),
          signal: controller.signal,
        });
        setDocumentos((prev) => [
          ...prev.filter((d) => d.tipo_documento_id !== tipo.id),
          documento,
        ]);
        toast.success(`${tipo.label} subido correctamente.`);
        onChangeRef.current?.();
      } catch (err) {
        // Cancelación del usuario — no es un error, no se notifica.
        if (!esCancelacion(err)) {
          toast.error(mensajeDeError(err, "No se pudo subir el documento."));
        }
      } finally {
        abortControllersRef.current.delete(tipo.id);
        setUploadingPercents((prev) => {
          const next = { ...prev };
          delete next[tipo.id];
          return next;
        });
      }
    },
    [personaUuid],
  );

  const cancelarSubida = useCallback((tipoId: number) => {
    abortControllersRef.current.get(tipoId)?.abort();
  }, []);

  const descargar = useCallback(
    async (tipo: DocumentoTipo, documento: PersonaDocumento) => {
      setBusyTipoId(tipo.id);
      try {
        await adapter.download(personaUuid, documento);
      } catch {
        toast.error("No se pudo descargar el documento.");
      } finally {
        setBusyTipoId(null);
      }
    },
    [personaUuid],
  );

  const eliminar = useCallback(
    async (tipo: DocumentoTipo, documento: PersonaDocumento) => {
      setBusyTipoId(tipo.id);
      try {
        await adapter.destroy(personaUuid, documento.id);
        setDocumentos((prev) => prev.filter((d) => d.id !== documento.id));
        toast.success(`${tipo.label} eliminado.`);
        onChangeRef.current?.();
      } catch {
        toast.error("No se pudo eliminar el documento.");
      } finally {
        setBusyTipoId(null);
      }
    },
    [personaUuid],
  );

  const guardarVigencia = useCallback(
    async (
      tipo: DocumentoTipo,
      documento: PersonaDocumento,
      opts: PersonaDocumentosVencimientoOptions,
    ) => {
      setBusyTipoId(tipo.id);
      try {
        const actualizado = await adapter.updateVencimiento(personaUuid, documento.id, opts);
        setDocumentos((prev) => [...prev.filter((d) => d.id !== documento.id), actualizado]);
        toast.success(`Vigencia de ${tipo.label} actualizada.`);
        onChangeRef.current?.();
      } finally {
        setBusyTipoId(null);
      }
    },
    [personaUuid],
  );

  return {
    slots,
    resumen,
    isLoading,
    loadError,
    recargar: cargar,
    busyTipoId,
    uploadingPercents,
    subir,
    cancelarSubida,
    descargar,
    eliminar,
    guardarVigencia,
    guardarExigidos,
  };
}
